
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  fetchDocuments,
  fetchDocumentById,
  createDocument,
  updateDocument,
  deleteDocument,
  fetchDocumentsByOrganization,
  fetchDocumentsByContact,
  fetchDocumentsByOpportunity,
  fetchDocumentPermissions,
  fetchDocumentVersions,
  addDocumentPermission,
  removeDocumentPermission,
  getDownloadUrl,
  DocumentInput,
  DocumentPermissionInput
} from "@/services/document/documentService";
import { DocumentWithRelations, DocumentVersionInfo } from '@/types/custom-types';
import { useToast } from "@/hooks/use-toast";

export function useDocuments() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: fetchDocuments
  });

  const getDocumentQuery = (id: string) => useQuery({
    queryKey: ['documents', id],
    queryFn: () => fetchDocumentById(id),
    enabled: !!id
  });

  const getDocumentsByOrganizationQuery = (organizationId: string) => useQuery({
    queryKey: ['documents', 'organization', organizationId],
    queryFn: () => fetchDocumentsByOrganization(organizationId),
    enabled: !!organizationId
  });

  const getDocumentsByContactQuery = (contactId: string) => useQuery({
    queryKey: ['documents', 'contact', contactId],
    queryFn: () => fetchDocumentsByContact(contactId),
    enabled: !!contactId
  });

  const getDocumentsByOpportunityQuery = (opportunityId: string) => useQuery({
    queryKey: ['documents', 'opportunity', opportunityId],
    queryFn: () => fetchDocumentsByOpportunity(opportunityId),
    enabled: !!opportunityId
  });

  const getDocumentPermissionsQuery = (documentId: string) => useQuery({
    queryKey: ['documents', documentId, 'permissions'],
    queryFn: () => fetchDocumentPermissions(documentId),
    enabled: !!documentId
  });
  
  const getDocumentVersionsQuery = (documentId: string) => useQuery({
    queryKey: ['documents', documentId, 'versions'],
    queryFn: () => fetchDocumentVersions(documentId),
    enabled: !!documentId
  });

  const createDocumentMutation = useMutation({
    mutationFn: ({ documentData, file }: { documentData: DocumentInput, file: File }) => 
      createDocument(documentData, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({
        title: "Document uploaded",
        description: "The document has been successfully uploaded."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to upload document",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const updateDocumentMutation = useMutation({
    mutationFn: ({ id, data, file }: { id: string, data: Partial<DocumentInput>, file?: File }) => 
      updateDocument(id, data, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents', variables.id] });
      toast({
        title: "Document updated",
        description: "The document has been successfully updated."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update document",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({
        title: "Document deleted",
        description: "The document has been successfully deleted."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete document",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const grantPermissionMutation = useMutation({
    mutationFn: ({ document_id, user_id, permission_type }: DocumentPermissionInput) => 
      addDocumentPermission(document_id, user_id, permission_type),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['documents', variables.document_id, 'permissions'] 
      });
      toast({
        title: "Permission granted",
        description: "User has been granted access to this document."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to grant permission",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const revokePermissionMutation = useMutation({
    mutationFn: removeDocumentPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({
        title: "Permission revoked",
        description: "User access has been revoked."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to revoke permission",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const getDownloadUrlQuery = (filePath: string) => useQuery({
    queryKey: ['document-url', filePath],
    queryFn: () => getDownloadUrl(filePath),
    enabled: !!filePath
  });

  return {
    documents: documentsQuery.data || [],
    isLoading: documentsQuery.isLoading,
    error: documentsQuery.error,
    getDocumentById: getDocumentQuery,
    getDocumentsByOrganization: getDocumentsByOrganizationQuery,
    getDocumentsByContact: getDocumentsByContactQuery,
    getDocumentsByOpportunity: getDocumentsByOpportunityQuery,
    getDocumentPermissions: getDocumentPermissionsQuery,
    getDocumentVersions: getDocumentVersionsQuery,
    createDocument: createDocumentMutation.mutate,
    updateDocument: updateDocumentMutation.mutate,
    deleteDocument: deleteDocumentMutation.mutate,
    grantPermission: grantPermissionMutation.mutate,
    revokePermission: revokePermissionMutation.mutate,
    getDownloadUrl: getDownloadUrlQuery,
  };
}
